import { Column } from 'primereact/column'
import { TreeNode } from 'primereact/treenode'
import { TreeTable, TreeTableExpandedKeysType, TreeTableSelectionEvent, TreeTableSelectionKeysType } from 'primereact/treetable'
import groupBy from 'lodash-es/groupBy'
import { useCallback, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { alertGreaterThan } from '@/utils/bodyRender'
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox'
import { Item } from '@/api'
import { Panel } from 'primereact/panel'

export interface ItemStats {
  '在庫切れ日数': number
  '在庫切れ個数': number
  '総出荷': number
  '総生産': number
}

export interface ItemTreeTableProps {
  items: Item[],
  itemStatus: Record<Item["品目ID"], Set<'empty' | 'little' | 'over'>>
  itemStats: Record<Item["品目ID"], ItemStats>
  onSelectionChange?: (items: Item[],isCategory: boolean) => void
}

interface FilterCheckboxes{
  'statusMinus': boolean
  'statusOver': boolean
  'statusLittle': boolean
}
/**
 * 品目マスタをツリーテーブルで表示するコンポーネント
 */
const ItemTreeTable = ({ items, itemStatus, itemStats,onSelectionChange }: ItemTreeTableProps) => {
  const [selection, setSelection] = useState<TreeTableSelectionKeysType>({})
  const [expandedKeys, setExpandedKeys] = useState<TreeTableExpandedKeysType>({})
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [filterCheckboxes, setFilterCheckboxes] = useState<FilterCheckboxes>({
    'statusMinus': false,
    'statusOver': false,
    'statusLittle': false,
  })

  const handleFilterCheckboxChange = (e: CheckboxChangeEvent) => {
    if(e.target.name){
      setFilterCheckboxes({...filterCheckboxes, [e.target.name]: e.checked})
    }
  }


  const filterKeyword = useCallback((item: Item) => {
    if (searchKeyword === '') return true
    if (item.分類 === undefined || String(item.分類) === '') return false
    // Unicode正規化
    const searchKeyword_N = searchKeyword.normalize('NFKC')
    const item_N = String(item.品目名).normalize('NFKC')
    const category_N = String(item.分類).normalize('NFKC')
    return String(item.品目ID).includes(searchKeyword_N) || item_N.includes(searchKeyword_N) || category_N.includes(searchKeyword_N)
  }
  , [searchKeyword])

  const itemFilter = useCallback((item: Item) => {
    const status = itemStatus[item.品目ID]
    if(filterCheckboxes.statusMinus && !(status?.has('empty'))) return false
    if(filterCheckboxes.statusLittle && !(status?.has('little'))) return false
    if(filterCheckboxes.statusOver && !(status?.has('over'))) return false
    return filterKeyword(item)
  }, [filterCheckboxes, filterKeyword])

  const treeNodes = useMemo((): TreeNode[] => {
    //あいまい検索
    const filteredItems = items.filter(itemFilter)
    const categories = groupBy(filteredItems, '分類') as Record<string, Item[]>
    return Object.keys(categories).map((category, index) => {
      const items = categories[category]
      const first = items[0] as Item
      return {
        key: `category-${items[0].分類}`,
        data: {
          category: `${category}(${items.length})`,
          行: first.行,
          在庫切れ日数: items.reduce((acc, item) => acc + itemStats[item.品目ID].在庫切れ日数!, 0),
          在庫切れ個数: items.reduce((acc, item) => acc + itemStats[item.品目ID].在庫切れ個数!, 0),
          総生産: items.reduce((acc, item) => acc + itemStats[item.品目ID].総生産!, 0),
          総出荷: items.reduce((acc, item) => acc + itemStats[item.品目ID].総出荷!, 0),
         },
        leaf: false,
        selectable: true,
        children: items.map(
          (item, index) =>
            ({
              key: `item-${item.品目ID}`,
              leaf: true,
              data: {
                ...item,
                在庫切れ日数: itemStats[item.品目ID].在庫切れ日数,
                在庫切れ個数: itemStats[item.品目ID].在庫切れ個数,
                総生産: itemStats[item.品目ID].総生産,
                総出荷: itemStats[item.品目ID].総出荷,
              },
              selectable: true,
            } satisfies TreeNode)
        ),
      } satisfies TreeNode
    })
  },[items, itemFilter,itemStats])

  const handleSelectionChange = (e: TreeTableSelectionEvent) =>{
    const selkeys = e.value as TreeTableSelectionKeysType
    let isCategory = false
    //配下の品目も選択状態にする
    for(const key in selkeys){
      if(key.startsWith('category-')){
        const category = key.replace('category-', '')
        items.filter(itemFilter).filter(item => item.分類 === category).forEach(item => {
          selkeys[`item-${item.品目ID}`] = true
        })
        isCategory = true
        break; //カテゴリ選択は一つだけ
      }
    }
    setSelection(selkeys as TreeTableSelectionKeysType)

    if(onSelectionChange){
      onSelectionChange(items.filter(item => selkeys[`item-${item.品目ID}`]),isCategory)
    }
  }

  const handleAllExpand = (expand: boolean) => () => {
    if(expand){
      const expandedKeys = treeNodes.reduce((acc, node) => {
        acc[node.key as keyof TreeTableExpandedKeysType] = true
        return acc
      }, {} as TreeTableExpandedKeysType)
      setExpandedKeys(expandedKeys)
    }else{
      setExpandedKeys({})
    }
  }
  const handleAllSelection = (selected: boolean) => () => {
    if(selected){
      setSelection({})
      if(onSelectionChange){
        onSelectionChange([],false)
      }
    }
  }

  const selectionCount = Object.keys(selection).length

  return (
    <Panel toggleable header="分類一覧" className="w-full">
    <div className="flex flex-column gap-2">
      <div className="flex align-items-center justify-start gap-2">
        <Button icon="pi pi-minus" size="small" tooltip="全て閉じる" tooltipOptions={{ position: 'top' }} onClick={handleAllExpand(false)}/>
        <Button icon="pi pi-plus" size="small" tooltip="全て開く" tooltipOptions={{ position: 'top' }} onClick={handleAllExpand(true)}/>
        <Button label={selection ? `選択中: ${selectionCount}件を解除` : '-'} size="small" disabled={selectionCount === 0} severity='info' onClick={handleAllSelection(true)}/>
      </div>
      <div className="flex flex-column gap-2">
        <div className="p-inputgroup">
          <span className="p-inputgroup-addon text-nowrap" style={{width: 100}}>検索</span>
          <InputText id="search" size="small" className='w-full' onChange={(e)=>setSearchKeyword(e.target.value)}/>
        </div>
        <div className="flex flex-wrap justify-content-start gap-3">
          <div className="flex align-items-center gap-2">
            <Checkbox name="statusMinus" onChange={handleFilterCheckboxChange} checked={filterCheckboxes.statusMinus} />
            <span>在庫切れ</span>
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox name="statusLittle" onChange={handleFilterCheckboxChange} checked={filterCheckboxes.statusLittle} />
            <span>在荷下限切れ</span>
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox name="statusOver" onChange={handleFilterCheckboxChange} checked={filterCheckboxes.statusOver} />
            <span>在庫上限超過</span>
          </div>
        </div>
      </div>

      <TreeTable
        paginator rows={25} rowsPerPageOptions={[10, 25, 50]}
        value={treeNodes}
        tableStyle={{ width: '100%' }}
        selectionKeys={selection}
        selectionMode="multiple"
        metaKeySelection={true}
        onSelectionChange={handleSelectionChange}

        columnResizeMode="expand"
        expandedKeys={expandedKeys} onToggle={(e) => setExpandedKeys(e.value)}
      >
        <Column
          field="category"
          header="分類"
          className="col-1"
          expander
          style={{
            overflow: 'visible',
            textWrap: 'nowrap',
          }}
        />
        <Column field="品目ID" header="ID" className="col-1" sortable/>
        <Column field="品目名" header="品目名" className="col-4" sortable/>
        <Column field="在庫切れ個数" header="在庫切れ" className="col-2" body={alertGreaterThan(0)} sortable/>
        <Column field="総出荷" header="総出荷" className="col-2" sortable/>
        <Column field="総生産" header="総生産" className="col-2" sortable/>
        <Column field="行" header="行" className="col-1" sortable headerTooltip='Excelファイル内の行番号'/>
      </TreeTable>
    </div>
    </Panel>
  )
}

export default ItemTreeTable
